/**
 * AtaChatInterface.tsx
 * Interface de chat pour la recherche dans les documentations techniques
 * via Google RAG (FileSearch). Stylisé avec CSS Modules + Fluent UI.
 */
import React, { useState, useEffect, useRef } from "react";
import { Spinner, SpinnerSize, IconButton, Stack, Text, Icon } from "@fluentui/react";
import ReactMarkdown from "react-markdown";
import { ChatMessage, GroundingChunk, RagStore } from "../../ata/types";
import { AtaModel, AtaQueryMode, QueryJobStepState } from "../../../services/ataService";
import styles from "./AtaChatInterface.module.css";

const CHUNK_DISPLAY_RE = /^(?<base>.+?)__p\d{4}-\d{4}\.txt(?:\s*[,.;:]\s*)?$/i;

function getLogicalSourceTitle(title?: string): string {
    if (!title) return "";
    const normalizedTitle = title.trim();
    const match = CHUNK_DISPLAY_RE.exec(normalizedTitle);
    return match?.groups?.base ?? title;
}

function findDocumentBySourceTitle(stores: RagStore[], sourceTitle?: string) {
    const logicalTitle = getLogicalSourceTitle(sourceTitle).toLowerCase();
    if (!logicalTitle) return undefined;
    return stores
        .flatMap(s => s.documents ?? [])
        .find(d => (d.displayName || "").toLowerCase() === logicalTitle || (d.name || "").toLowerCase() === logicalTitle);
}

interface DocView {
    title: string;
    url?: string;
    excerpt?: string;
    page?: number;
}

function extractPage(text: string | undefined): number | undefined {
    if (!text) return undefined;
    const m = text.match(/\bpage\s+(\d+)\b|\bp\.?\s*(\d+)\b|\bpg\.?\s*(\d+)\b/i);
    if (!m) return undefined;
    const n = parseInt(m[1] ?? m[2] ?? m[3] ?? "");
    return isNaN(n) ? undefined : n;
}

function getUniqueSourceChunks(chunks: GroundingChunk[] | undefined): Array<{ chunk: GroundingChunk; index: number }> {
    if (!chunks || chunks.length === 0) return [];

    const seen = new Set<string>();
    const unique: Array<{ chunk: GroundingChunk; index: number }> = [];

    for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const logicalTitle = getLogicalSourceTitle(chunk.title).trim().toLowerCase();
        const page = chunk.page ?? extractPage(chunk.text);
        const uri = (chunk.uri || "").trim().toLowerCase();
        const textFingerprint = (chunk.text || "").replace(/\s+/g, " ").trim().slice(0, 160).toLowerCase();
        const key = `${logicalTitle}||${page ?? ""}||${uri}||${textFingerprint}`;

        if (seen.has(key)) continue;
        seen.add(key);
        unique.push({ chunk, index: i });
    }

    return unique;
}

/**
 * Replaces "– Page N (filename.pdf)" patterns inside answer text with Markdown links
 * of the form [– Page N (filename.pdf)](pageref:blobPath:N) so that makeCiteComponents
 * can render them as clickable buttons opening the viewer at the right page.
 */
function preprocessPageRefs(text: string, stores: RagStore[]): string {
    const fileIndex = new Map<string, string>(); // displayName lowercase → blobPath
    for (const store of stores) {
        for (const doc of store.documents ?? []) {
            if (doc.blobPath) fileIndex.set(doc.displayName.toLowerCase(), doc.blobPath);
        }
    }
    if (fileIndex.size === 0) return text;

    // Matches: – Page(s) N[, M][ et P] (filename[.(nested)].pdf)
    // Handles filenames with one level of inner parentheses e.g. "WIPOTEC (pesage) - ...pdf"
    return text.replace(
        /([\u2013\-]\s*Pages?\s+)([\d][\d,\s]*(?:et\s+\d+)?)\s*\(([^()]*(?:\([^()]*\)[^()]*)*\.pdf)\)/gi,
        (match, _prefix, pagesPart, filename) => {
            const blobPath = fileIndex.get(filename.trim().toLowerCase());
            if (!blobPath) return match;
            const firstPage = parseInt(pagesPart.match(/\d+/)?.[0] ?? "0");
            if (!firstPage) return match;
            return `[${match}](pageref:${blobPath}:${firstPage})`;
        }
    );
}

/**
 * Returns a ReactMarkdown `components` map that converts `[ⁿ](cite:idx)` links
 * (inserted by the backend) into clickable inline superscript citation buttons,
 * and `(pageref:blobPath:N)` links into inline page-reference buttons.
 */
function makeCiteComponents(groundingChunks: GroundingChunk[] | undefined, stores: RagStore[], onDocView: (dv: DocView) => void) {
    return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        a({ href, children }: { href?: string; children?: React.ReactNode; [k: string]: any }) {
            if (href?.startsWith("cite:")) {
                const idx = parseInt(href.slice(5));
                const chunk = groundingChunks?.[idx];
                if (chunk) {
                    const matchedDoc = findDocumentBySourceTitle(stores, chunk.title);
                    const blobPath = matchedDoc?.blobPath;
                    const page = chunk.page;
                    const fileUrl = blobPath ? `/content/${blobPath}${page ? `#page=${page}` : ""}` : undefined;
                    const sourceTitle = getLogicalSourceTitle(chunk.title) || `Source ${idx + 1}`;
                    return (
                        <sup>
                            <button
                                className={styles.citationLink}
                                onClick={() =>
                                    onDocView({
                                        title: sourceTitle,
                                        url: fileUrl,
                                        excerpt: chunk.text,
                                        page
                                    })
                                }
                                title={`${sourceTitle}${page ? ` — p.\u00a0${page}` : ""}`}
                            >
                                {children}
                            </button>
                        </sup>
                    );
                }
            }
            if (href?.startsWith("pageref:")) {
                // Format: pageref:<blobPath>:<page>
                const withoutScheme = href.slice(8);
                const lastColon = withoutScheme.lastIndexOf(":");
                const blobPath = withoutScheme.slice(0, lastColon);
                const page = parseInt(withoutScheme.slice(lastColon + 1));
                const fileUrl = blobPath ? `/content/${blobPath}#page=${page}` : undefined;
                const filename = blobPath.split("/").pop() ?? blobPath;
                return (
                    <button
                        className={styles.pageRefLink}
                        onClick={() => onDocView({ title: filename, url: fileUrl, page })}
                        title={`Ouvrir ${filename}\u00a0— page\u00a0${page}`}
                    >
                        {children}
                    </button>
                );
            }
            return (
                <a href={href} target="_blank" rel="noreferrer">
                    {children}
                </a>
            );
        }
    };
}

interface AtaChatInterfaceProps {
    history: ChatMessage[];
    isQueryLoading: boolean;
    queryStageLabel?: string;
    queryStepStates?: QueryJobStepState[];
    queryMode: AtaQueryMode;
    onChangeQueryMode: (mode: AtaQueryMode) => void;
    queryModel: AtaModel;
    onChangeQueryModel: (model: AtaModel) => void;
    onSendMessage: (message: string) => void;
    onNewChat: () => void;
    availableStores: RagStore[];
    selectedStoreNames: string[];
    selectedDocumentNames: string[];
    onToggleStore: (name: string) => void;
    onToggleDocument: (name: string) => void;
}

const AtaChatInterface: React.FC<AtaChatInterfaceProps> = ({
    history,
    isQueryLoading,
    queryStageLabel,
    queryStepStates,
    queryMode,
    onChangeQueryMode,
    queryModel,
    onChangeQueryModel,
    onSendMessage,
    onNewChat,
    availableStores,
    selectedStoreNames,
    selectedDocumentNames,
    onToggleStore,
    onToggleDocument
}) => {
    const [query, setQuery] = useState("");
    const [docView, setDocView] = useState<DocView | null>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [expandedStepIdx, setExpandedStepIdx] = useState<Set<number>>(new Set());
    const [hasManualStepToggle, setHasManualStepToggle] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [history, isQueryLoading]);

    useEffect(() => {
        if (!isQueryLoading) {
            setExpandedStepIdx(new Set());
            setHasManualStepToggle(false);
        }
    }, [isQueryLoading]);

    useEffect(() => {
        if (!isQueryLoading || hasManualStepToggle || !Array.isArray(queryStepStates) || queryStepStates.length === 0) {
            return;
        }

        let activeIdx = queryStepStates.findIndex(step => step.status === "in_progress");
        if (activeIdx < 0) {
            activeIdx = queryStepStates.findIndex(step => step.status === "pending");
        }
        if (activeIdx < 0) {
            activeIdx = queryStepStates.length - 1;
        }

        if (activeIdx >= 0) {
            setExpandedStepIdx(new Set([activeIdx]));
        }
    }, [isQueryLoading, queryStepStates, hasManualStepToggle]);

    const toggleStepDetails = (idx: number) => {
        setHasManualStepToggle(true);
        setExpandedStepIdx(prev => {
            const next = new Set(prev);
            if (next.has(idx)) {
                next.delete(idx);
            } else {
                next.add(idx);
            }
            return next;
        });
    };

    const getStepStatusLabel = (status: QueryJobStepState["status"]): string => {
        switch (status) {
            case "done":
                return "Terminé";
            case "in_progress":
                return "En cours";
            case "error":
                return "Erreur";
            default:
                return "En attente";
        }
    };

    const getStepStatusClassName = (status: QueryJobStepState["status"]): string => {
        switch (status) {
            case "done":
                return styles.stepStatusDone;
            case "in_progress":
                return styles.stepStatusInProgress;
            case "error":
                return styles.stepStatusError;
            default:
                return styles.stepStatusPending;
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (query.trim()) {
            onSendMessage(query);
            setQuery("");
        }
    };

    const selectionCount = selectedStoreNames.length + selectedDocumentNames.length;

    return (
        <div className={styles.root}>
            {/* Toolbar */}
            <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                    <span className={styles.modeLabel}>Mode :</span>
                    <div className={styles.modeSwitch}>
                        <button
                            className={`${styles.modeBtn} ${queryMode === "question" ? styles.modeBtnActive : ""}`}
                            type="button"
                            onClick={() => onChangeQueryMode("question")}
                            disabled={isQueryLoading}
                            title="Question: chat rapide avec prompt prédéfini"
                        >
                            Question
                        </button>
                        <button
                            className={`${styles.modeBtn} ${queryMode === "agent" ? styles.modeBtnActive : ""}`}
                            type="button"
                            onClick={() => onChangeQueryMode("agent")}
                            disabled={isQueryLoading}
                            title="Agent: investigation multi-étapes pilotée par Gemini"
                        >
                            Agent
                        </button>
                    </div>
                    <span className={styles.modeLabel}>Modèle :</span>
                    <select
                        className={styles.modelSelect}
                        value={queryModel}
                        onChange={e => onChangeQueryModel(e.target.value as AtaModel)}
                        disabled={isQueryLoading}
                        title="Choisir le modèle Gemini pour les modes Question et Agent"
                    >
                        <option value="gemini-3-flash-preview">gemini-3-flash-preview</option>
                        <option value="gemini-3.1-pro-preview">gemini-3.1-pro-preview</option>
                    </select>
                </div>
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <button className={styles.toolbarBtn} onClick={onNewChat} disabled={history.length === 0 || isQueryLoading} title="Nouvelle conversation">
                        <Icon iconName="Refresh" className={styles.btnIcon} />
                        Nouvelle conversation
                    </button>
                    <button
                        className={`${styles.toolbarBtn} ${isPanelOpen ? styles.toolbarBtnActive : ""}`}
                        onClick={() => setIsPanelOpen(!isPanelOpen)}
                        title="Sélectionner les sources"
                    >
                        <Icon iconName="FilterSettings" className={styles.btnIcon} />
                        {`Sources${selectionCount > 0 ? ` (${selectionCount})` : ""}`}
                    </button>
                </Stack>
            </div>

            <div className={styles.body}>
                {/* Chat area */}
                <div className={styles.chatArea}>
                    {history.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIconWrap}>
                                <Icon iconName="DocumentSearch" />
                            </div>
                            <Text variant="xxLarge" className={styles.emptyTitle}>
                                Documentation Technique
                            </Text>
                            <Text variant="mediumPlus" className={styles.emptySubtitle}>
                                Posez vos questions sur vos manuels et schémas électriques
                            </Text>
                            {selectionCount === 0 && (
                                <Text className={styles.noSelectionHint}>
                                    Aucune source sélectionnée: la recherche portera sur toutes les sources disponibles.
                                </Text>
                            )}
                        </div>
                    ) : (
                        <div className={styles.messageStream}>
                            {history.map((msg, i) => (
                                <div key={i} className={`${styles.messageRow} ${msg.role === "user" ? styles.messageRowUser : styles.messageRowModel}`}>
                                    {msg.role === "model" && (
                                        <div className={styles.avatar}>
                                            <Icon iconName="Robot" />
                                        </div>
                                    )}
                                    <div className={`${styles.bubble} ${msg.role === "user" ? styles.bubbleUser : styles.bubbleModel}`}>
                                        <div className={styles.bubbleContent}>
                                            <ReactMarkdown>
                                                {msg.role === "model" ? msg.parts[0].text.replace(/\[[^\]]*\]\(cite:\d+\)/g, "") : msg.parts[0].text}
                                            </ReactMarkdown>
                                        </div>
                                        {msg.role === "model" && msg.groundingChunks && msg.groundingChunks.length > 0 && (
                                            <div className={styles.sources}>
                                                <Text className={styles.sourcesLabel}>Sources :</Text>
                                                <div className={styles.sourcesBtns}>
                                                    {getUniqueSourceChunks(msg.groundingChunks).map(({ chunk, index: ci }) => {
                                                        const rawLabel = getLogicalSourceTitle(chunk.title) || `Source ${ci + 1}`;
                                                        const matchedDoc = findDocumentBySourceTitle(availableStores, chunk.title);
                                                        const blobPath = matchedDoc?.blobPath;
                                                        const page = chunk.page ?? extractPage(chunk.text);
                                                        const labelBase = rawLabel.length > 36 ? rawLabel.slice(0, 34) + "…" : rawLabel;
                                                        const label = page ? `${labelBase} (p. ${page})` : labelBase;
                                                        const fileUrl = blobPath ? `/content/${blobPath}${page ? `#page=${page}` : ""}` : undefined;
                                                        return (
                                                            <button
                                                                key={ci}
                                                                className={`${styles.sourceBtn} ${blobPath ? styles.sourceBtnDoc : ""}`}
                                                                title={
                                                                    blobPath
                                                                        ? `Ouvrir «\u00a0${rawLabel}\u00a0»${page ? ` — page\u00a0${page}` : ""}`
                                                                        : `Afficher l'extrait : ${rawLabel}`
                                                                }
                                                                onClick={() =>
                                                                    setDocView({
                                                                        title: rawLabel,
                                                                        url: fileUrl,
                                                                        excerpt: chunk.text || "",
                                                                        page
                                                                    })
                                                                }
                                                            >
                                                                <span className={styles.sourceBtnNum}>[{ci + 1}]</span>
                                                                <Icon iconName={blobPath ? "OpenFile" : "TextDocument"} /> {label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {isQueryLoading && (
                                <div className={`${styles.messageRow} ${styles.messageRowModel}`}>
                                    <div className={styles.avatar}>
                                        <Icon iconName="Robot" />
                                    </div>
                                    <div className={`${styles.bubble} ${styles.bubbleModel}`}>
                                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={styles.queryProgressHeader}>
                                            <Spinner size={SpinnerSize.small} />
                                            <Text className={styles.generatingText}>{queryStageLabel || "Analyse en cours…"}</Text>
                                        </Stack>
                                        {Array.isArray(queryStepStates) && queryStepStates.length > 0 && (
                                            <div className={styles.stepTracker}>
                                                {queryStepStates.map((step, idx) => {
                                                    const isExpanded = expandedStepIdx.has(idx);
                                                    const safeResult = String(step.result || "").trim();
                                                    return (
                                                        <div key={`${step.name}-${idx}`} className={styles.stepCard}>
                                                            <div className={styles.stepHeader}>
                                                                <div className={styles.stepTitleWrap}>
                                                                    <span className={styles.stepIndex}>{idx + 1}.</span>
                                                                    <span className={styles.stepTitle}>{step.name}</span>
                                                                </div>
                                                                <div className={styles.stepActions}>
                                                                    <span className={`${styles.stepStatusBadge} ${getStepStatusClassName(step.status)}`}>
                                                                        {getStepStatusLabel(step.status)}
                                                                    </span>
                                                                    {safeResult && (
                                                                        <button
                                                                            type="button"
                                                                            className={styles.stepToggleBtn}
                                                                            onClick={() => toggleStepDetails(idx)}
                                                                        >
                                                                            {isExpanded ? "Masquer" : "Afficher"}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {isExpanded && safeResult && <div className={styles.stepResult}>{safeResult}</div>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                    )}

                    {/* Input bar */}
                    <div className={styles.inputBar}>
                        <form onSubmit={handleSubmit} className={styles.inputForm}>
                            <input
                                type="text"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder={
                                    selectionCount === 0 ? "Posez une question (toutes les sources seront utilisées)…" : "Posez une question sur vos documents…"
                                }
                                className={styles.input}
                                disabled={isQueryLoading}
                            />
                            <button type="submit" disabled={isQueryLoading || !query.trim()} className={styles.sendBtn} title="Envoyer">
                                <Icon iconName="Send" />
                            </button>
                        </form>
                    </div>
                </div>

                {/* Sources sidebar */}
                {isPanelOpen && (
                    <aside className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <Text variant="mediumPlus" className={styles.panelTitle}>
                                Sources
                            </Text>
                            <IconButton
                                iconProps={{ iconName: "ChevronRight" }}
                                onClick={() => setIsPanelOpen(false)}
                                title="Fermer le panneau"
                                styles={{ root: { color: "#605e5c" } }}
                            />
                        </div>
                        <div className={styles.panelBody}>
                            <Text className={styles.panelInstruction}>
                                Cochez les catégories ou documents à inclure dans la recherche. Sans sélection, toutes les sources disponibles sont utilisées.
                            </Text>
                            {availableStores.length === 0 ? (
                                <Text className={styles.panelEmpty}>
                                    Aucune catégorie disponible.
                                    <br />
                                    Contactez votre administrateur pour obtenir l'accès.
                                </Text>
                            ) : (
                                <div className={styles.storeList}>
                                    {availableStores.map(store => (
                                        <div key={store.displayName} className={styles.storeGroup}>
                                            {store.storeExists === false ? (
                                                <div className={styles.storeLabel} style={{ opacity: 0.5, cursor: "default" }}>
                                                    <Icon iconName="Warning" className={styles.folderIcon} style={{ color: "#ca5010" }} />
                                                    <span className={styles.storeLabelText}>{store.displayName}</span>
                                                    <span style={{ fontSize: 10, color: "#ca5010", marginLeft: 6 }}>Non configur&eacute;</span>
                                                </div>
                                            ) : (
                                                <label className={styles.storeLabel}>
                                                    <input
                                                        type="checkbox"
                                                        className={styles.checkbox}
                                                        checked={selectedStoreNames.includes(store.name!)}
                                                        onChange={() => onToggleStore(store.name!)}
                                                    />
                                                    <Icon iconName="FolderFill" className={styles.folderIcon} />
                                                    <span className={styles.storeLabelText}>{store.displayName}</span>
                                                </label>
                                            )}
                                            {store.documents && store.documents.length > 0 && (
                                                <div className={styles.docIndent}>
                                                    {store.documents.map(doc => (
                                                        <label key={doc.name} className={styles.docLabel}>
                                                            <input
                                                                type="checkbox"
                                                                className={styles.checkbox}
                                                                checked={selectedDocumentNames.includes(doc.name)}
                                                                onChange={() => onToggleDocument(doc.name)}
                                                            />
                                                            <Icon iconName="TextDocument" className={styles.docFileIcon} />
                                                            <span className={styles.docLabelText} title={doc.displayName}>
                                                                {doc.displayName}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </aside>
                )}
            </div>

            {/* Document viewer overlay */}
            {docView && (
                <div className={styles.docViewerBackdrop} onClick={() => setDocView(null)}>
                    <div className={styles.docViewer} onClick={e => e.stopPropagation()}>
                        <div className={styles.docViewerHeader}>
                            <Icon iconName={docView.url ? "OpenFile" : "TextDocument"} className={styles.docViewerIcon} />
                            <span className={styles.docViewerTitle} title={docView.title}>
                                {docView.title}
                            </span>
                            {docView.page && <span className={styles.docViewerPage}>page&nbsp;{docView.page}</span>}
                            {docView.url && (
                                <a href={docView.url} target="_blank" rel="noreferrer" className={styles.docViewerNewTab} title="Ouvrir dans un nouvel onglet">
                                    <Icon iconName="OpenInNewTab" />
                                </a>
                            )}
                            <button className={styles.docViewerClose} onClick={() => setDocView(null)} title="Fermer">
                                <Icon iconName="Cancel" />
                            </button>
                        </div>
                        {docView.url ? (
                            <iframe src={docView.url} className={styles.docViewerFrame} title={docView.title} />
                        ) : (
                            <div className={styles.docViewerExcerpt}>
                                {docView.excerpt ? (
                                    <ReactMarkdown>{docView.excerpt}</ReactMarkdown>
                                ) : (
                                    <Text style={{ color: "#605e5c", fontStyle: "italic" }}>Aucun extrait disponible.</Text>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AtaChatInterface;
